"use client";

import { LinkIcon, UnlinkIcon } from "@primer/octicons-react";
import React, { useMemo, useState } from "react";
import { Container, Spinner, Table } from "reactstrap";
import Button from "~/components/Button";
import FormModal from "~/components/FormModal";
import TextField from "~/components/TextField";
import {
  Subscription,
  SubscriptionCustomer,
} from "~/gen/rpc/stripe-subscriptions";
import useApi from "~/hooks/use-api";
import { Organization } from "../../orgs";

interface EditSubscriptionModalProps {
  subscription: Subscription | null;
  onSave: () => void;
  onClose: () => void;
}

async function unlinkSubscription(subscriptionId: string) {
  try {
    const response = await fetch(
      `/api/stripe/subscription/${subscriptionId}/unlink`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          organizationId: null,
          locationId: null,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        `Failed to unlink subscription: ${errorText || response.statusText}`
      );
    }
  } catch (error) {
    alert(
      error instanceof Error
        ? `Failed to unlink subscription: ${error.message}`
        : "Failed to unlink subscription. Please try again or alert Engineering."
    );
  }
}

function displayCustomer(customer: SubscriptionCustomer): string {
  if (customer.email && customer.name)
    return `${customer.name}\n${customer.email}`;
  if (customer.name) return customer.name;
  if (customer.email) return customer.email;
  return customer.id;
}

function EditSubscriptionModal(props: EditSubscriptionModalProps) {
  const { subscription, onSave, onClose } = props;

  if (!subscription) return null;

  return (
    <FormModal
      title="Link Subscription"
      open={Boolean(subscription)}
      onSave={onSave}
      onClose={onClose}
      path={`/api/stripe/subscription/${subscription?.id}/link`}
      initValues={{
        organizationId: subscription?.organizationId,
        locationId: subscription?.locationId,
      }}
      new
    >
      <p>
        {
          "Please provide the Organization ID & Location ID that you would like to link this subscription to."
        }
      </p>
      <TextField
        label="Organization ID"
        placeholder="e.g. 01CM748PB4AHZ9B2A7HQFJYZ1J"
        name="organizationId"
        type="text"
      />
      <TextField
        label="Location ID"
        placeholder="e.g. 01ANQVFDWRSNJK5KS6HFKRP8RA"
        name="locationId"
        type="text"
      />
    </FormModal>
  );
}

export default function StripeSubscriptionsPage() {
  const {
    data: subscriptions,
    revalidate,
    isValidating: isValidatingSubscriptions,
  } = useApi<Subscription[]>("/api/stripe/subscription");

  const { unlinkedSubscriptions, linkedSubscriptions } = useMemo(() => {
    if (!subscriptions) {
      return { unlinkedSubscriptions: [], linkedSubscriptions: [] };
    }

    const unlinked: Subscription[] = [];
    const linked: Subscription[] = [];

    for (const subscription of subscriptions) {
      if (subscription.locationId) {
        linked.push(subscription);
      } else {
        unlinked.push(subscription);
      }
    }

    return { unlinkedSubscriptions: unlinked, linkedSubscriptions: linked };
  }, [subscriptions]);

  const { data: organizations, isValidating: isValidatingOrganizations } =
    useApi<Organization[]>("/api/organization?includeDisabled=true", {});

  const organizationsMap = useMemo(() => {
    if (!organizations) return new Map<string, Organization>();

    return new Map(organizations.map((org) => [org.uid, org]));
  }, [organizations]);

  const [currentSubscription, setCurrentSubscription] =
    useState<Subscription | null>(null);

  const isValidating = isValidatingSubscriptions || isValidatingOrganizations;

  return (
    <Container>
      <h1>Stripe Subscriptions {isValidating && <Spinner />}</h1>

      <br />
      <h3>Unlinked Subscriptions</h3>
      <br />

      <Table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Customer</th>
            <th>Status</th>
            <th>Description</th>
            <th>Link</th>
          </tr>
        </thead>
        <tbody>
          {unlinkedSubscriptions.map((s) => (
            <tr key={s.id}>
              <td>{s.id}</td>
              <td>{displayCustomer(s.stripeCustomer)}</td>
              <td>{s.status}</td>
              <td
                style={{
                  maxWidth: "200px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {s.description}
              </td>
              <td>
                <Button
                  onClick={() => {
                    setCurrentSubscription(s);
                  }}
                  style={{ border: "none" }}
                >
                  <LinkIcon size={20} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <br />
      <h3>Linked Subscriptions</h3>
      <br />

      <Table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Customer</th>
            <th>Organization & Location</th>
            <th>Status</th>
            <th>Description</th>
            <th>Unlink</th>
          </tr>
        </thead>
        <tbody>
          {linkedSubscriptions.map((s) => {
            const organization = organizationsMap.get(s.organizationId ?? "");

            return (
              <tr key={s.id}>
                <td>{s.id}</td>
                <td>{displayCustomer(s.stripeCustomer)}</td>
                <td>
                  <a href={`/orgs/${s.organizationId}`}>
                    {organization?.name ?? s.organizationId}
                  </a>
                  <br />
                  <a
                    href={`/orgs/${s.organizationId}/locations/${s.locationId}`}
                  >
                    {s.locationId}
                  </a>
                </td>
                <td>{s.status}</td>
                <td
                  style={{
                    maxWidth: "200px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.description}
                </td>
                <td>
                  <Button
                    onClick={async () => {
                      setCurrentSubscription(null);
                      if (
                        window.confirm(
                          `Do you want to unlink this subscription? It's currently linked to: \nOrganization: ${organization?.name} (${s.organizationId})\nLocation ID: ${s.locationId}.`
                        )
                      ) {
                        await unlinkSubscription(s.id);
                        revalidate();
                      }
                    }}
                    style={{ border: "none" }}
                  >
                    <UnlinkIcon size={20} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <EditSubscriptionModal
        subscription={currentSubscription}
        onClose={() => setCurrentSubscription(null)}
        onSave={() => {
          revalidate();
          setCurrentSubscription(null);
        }}
      />
    </Container>
  );
}
